import { useState, useCallback, useRef, useEffect } from 'react';
import { sendCasualMessage, sendAgenticMessage, sendAgenticMessageWithAttachments, generateTTS, getMessageHistory } from '../services/api';

export function useChat() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState('agentic');
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  const timersRef = useRef([]);
  
  useEffect(() => {
    console.log('[useChat] Animation Phase:', animationPhase);
  }, [animationPhase]);

  const clearPhaseTimers = useCallback(() => {
    timersRef.current.forEach((timerId) => clearTimeout(timerId));
    timersRef.current = [];
  }, []);

  const setPhaseTimer = useCallback((cb, ms) => {
    const timerId = setTimeout(cb, ms);
    timersRef.current.push(timerId);
    return timerId;
  }, []);

  const cleanAIMessage = (text) => {
    if (!text) return 'No response';
    let cleaned = text;
    // Remove objectId artifacts
    cleaned = cleaned.replace(/\[\{[^[\]]*?'objectId'[^[\]]*?\}\],?/g, '');
    // Remove code blocks that look like terminal/shell output
    cleaned = cleaned.replace(/```(?:bash|shell|sh|terminal|console|log)\n[\s\S]*?```/g, '');
    // Remove file path references
    cleaned = cleaned.replace(/(?:\/(?:workspace|tmp|opt|home|etc|var)\/[^\s)}\]]+)/g, '[file]');
    // Remove API key/token patterns
    cleaned = cleaned.replace(/(?:sk-|key-|token-)[a-zA-Z0-9_-]{20,}/g, '[key]');
    return cleaned.trim() || 'Done!';
  };

  const getFallbackReply = useCallback((userText) => {
    const intro = mode === 'agentic'
      ? 'Agentic preview reply'
      : 'Casual preview reply';
    return `${intro}: I received "${userText}". Backend is unavailable right now, but the full animation flow is active for testing.`;
  }, [mode]);

  const loadHistory = useCallback(async () => {
    try {
      const history = await getMessageHistory(mode);
      if (Array.isArray(history) && history.length > 0) {
        const formatted = [];
        history.forEach((msg, i) => {
          if (msg.user_message) {
            formatted.push({
              id: Date.now() + i * 2,
              role: 'user', content: msg.user_message,
              timestamp: msg.created_at || new Date().toISOString(),
              attachments: msg.attachments || [],
            });
          }
          if (msg.ai_message) {
            formatted.push({
              id: Date.now() + i * 2 + 1,
              role: 'assistant', content: cleanAIMessage(msg.ai_message),
              timestamp: msg.created_at || new Date().toISOString(),
            });
          }
        });
        if (formatted.length > 0) setMessages(formatted);
      }
    } catch (e) {
      console.log('No chat history available');
    }
  }, [mode]);

  const playAudio = useCallback((url) => {
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(url);
    audioRef.current = audio;
    setIsAudioPlaying(true);
    
    audio.onended = () => setIsAudioPlaying(false);
    audio.onpause = () => setIsAudioPlaying(false);
    audio.onerror = () => setIsAudioPlaying(false);

    audio.play().catch(e => {
      console.error('Audio play error:', e);
      setIsAudioPlaying(false);
    });
    return audio;
  }, []);

  const sendMessage = useCallback(async (text, inputType = 'text', attachments = []) => {
    if ((!text.trim() && attachments.length === 0) || isLoading) return;

    clearPhaseTimers();

    // Create message with attachment info for display
    const userMsg = {
      id: Date.now(), role: 'user', content: text.trim(),
      inputType, timestamp: new Date().toISOString(),
      attachments: attachments.map(a => ({
        name: a.name,
        type: a.type,
        size: a.size,
        preview: a.preview, // Only for images
      })),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    const sendStartedAt = Date.now();
    const glowStartMs = 300;
    const glowDurationMs = 2400;
    const sparkleStartMs = glowStartMs + glowDurationMs;
    const sparkleDurationMs = 2800;
    const minTotalBeforeRespondMs = sparkleStartMs + sparkleDurationMs;
    const sparkleFadeMs = 1100;

    setAnimationPhase('sending');
    setPhaseTimer(() => setAnimationPhase('glowing'), glowStartMs);
    setPhaseTimer(() => setAnimationPhase('sparkling'), sparkleStartMs);

    try {
      let response;
      let usedFallback = false;
      try {
        if (mode === 'casual') {
          response = await sendCasualMessage(text.trim(), inputType === 'voice' ? 'voice' : 'text');
        } else {
          // Agentic mode - check for attachments
          if (attachments.length > 0) {
            response = await sendAgenticMessageWithAttachments(text.trim(), attachments);
          } else {
            response = await sendAgenticMessage(text.trim());
          }
        }
      } catch (apiError) {
        console.warn('Chat API unavailable, using fallback response for UI testing.', apiError);
        usedFallback = true;
        response = { ai_message: getFallbackReply(text.trim()) };
      }

      const cleanMessage = cleanAIMessage(response?.ai_message || getFallbackReply(text.trim()));
      const elapsedSinceSend = Date.now() - sendStartedAt;
      const waitForGlowAndSparkle = Math.max(0, minTotalBeforeRespondMs - elapsedSinceSend);

      setPhaseTimer(() => {
        setAnimationPhase('responding');
        // Keep sparkle visible during `responding` for a clean fade, then reveal text.
        setPhaseTimer(() => {
          const aiMsg = {
            id: Date.now() + 1, role: 'assistant', content: cleanMessage,
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [...prev, aiMsg]);

          if (inputType === 'voice' && response.ai_message && !usedFallback) {
            generateTTS(response.ai_message).then(tts => {
              if (tts.audio_url) playAudio(tts.audio_url);
            }).catch(console.error);
          }

          setPhaseTimer(() => {
            setAnimationPhase('idle');
            setIsLoading(false);
          }, 900);
        }, sparkleFadeMs);
      }, waitForGlowAndSparkle);

    } catch (e) {
      console.error('Chat error:', e);
      setMessages(prev => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: 'Something went wrong. Please try again.', timestamp: new Date().toISOString() },
      ]);
      setAnimationPhase('idle');
      setIsLoading(false);
    }
  }, [mode, isLoading, playAudio, clearPhaseTimers, setPhaseTimer, getFallbackReply]);

  useEffect(() => {
    return () => clearPhaseTimers();
  }, [clearPhaseTimers]);

  const clearMessages = useCallback(() => setMessages([]), []);

  return {
    messages, isLoading, mode, setMode,
    sendMessage, clearMessages, animationPhase,
    playAudio, loadHistory, isAudioPlaying
  };
}
